import { Location } from '@angular/common';
import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { filter } from 'rxjs/operators';

import { AuthErrorModalComponent } from '../components/auth-error-modal/auth-error-modal.component';
import { AuthErrorType } from '../components/auth-error-display/auth-error-display.component';
import { openSupportPanel } from '../util/support-panel';

interface AuthErrorModalNavigationState {
  authErrorModal?: {
    type?: AuthErrorType;
    title?: string;
    message?: string;
    attemptedUrl?: string;
    openedAt?: number;
  };
}

@Injectable({ providedIn: 'root' })
export class AuthErrorModalService {
  private initialized = false;
  private lastOpenedAt?: number;
  private modalRef?: NgbModalRef;

  constructor(
    private modal: NgbModal,
    private router: Router,
    private location: Location,
    @Inject(DOCUMENT) private document: Document,
  ) { }

  init(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => this.openModalFromNavigationState());
  }

  private openModalFromNavigationState(): void {
    const state = this.location.getState() as AuthErrorModalNavigationState;
    const modalState = state.authErrorModal;

    if (!modalState || modalState.openedAt === this.lastOpenedAt || this.modalRef) {
      return;
    }

    this.lastOpenedAt = modalState.openedAt;
    this.location.replaceState(this.router.url);
    const ref = this.modal.open(AuthErrorModalComponent, { centered: true });
    ref.componentInstance.type = modalState.type ?? 'error';
    ref.componentInstance.title = modalState.title;
    ref.componentInstance.message = modalState.message;
    this.modalRef = ref;

    ref.result
      .then(shouldContactSupport => {
        if (shouldContactSupport) {
          setTimeout(() => {
            const defaultType = modalState.type ?? 'error';
            const config = ref.componentInstance.config;
            openSupportPanel(this.document, {
              source: 'auth-error',
              type: defaultType,
              title: modalState.title ?? config.title,
              message: modalState.message ?? config.message,
              attemptedUrl: modalState.attemptedUrl,
              createdAt: new Date().toISOString(),
            });
          });
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (this.modalRef === ref) {
          this.modalRef = undefined;
        }
      });
  }
}
